import React, { useEffect, useLayoutEffect, useImperativeHandle, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit'
import { Offcanvas } from 'react-bootstrap'
import { FieldArray, useFormikContext } from 'formik'
import { isEqual } from 'lodash'

import { modifyArticle, bulkOperateFigures } from '../articleAPI'
import { isAreaArticleType } from '../ArticleParse'
import { useFormikReset } from '../CommonComponents'
import {
  getParagraphStyleName,
  getSubmitParamForArticleUpsert,
  nullishValueToInternalValue,
  nullishValueToValue
} from '..'
import { FigureFields } from '../articleFigures/FigureFields'
import { HalfSizeImageCharacterSettingFields } from '../articleFigures/HalfSizeImageCharacterSettingFields'
import { ArticleNameTitle } from '../ArticleNameTitle'
import { Article, Figure, HalfSizeImageCharacterSetting, Layoutable } from '../types'

// TODO: set disabled attributes

interface EnumValues {
  figureHorizontalPositionBases: Record<string, string>
  figureVerticalPositionBases: Record<string, string>
  figureTypes: Record<string, string>
  figureOrientations: Record<string, string>
  textFlowPositions: Record<string, string>
  captionPositions: Record<string, string>
}

interface Enums {
  positionBases: {
    horizontalPositionBase: Record<string, string>
    verticalPositionBase: Record<string, string>
  }
  positionUnitName: {
    verticalPosition: string
    horizontalPosition: string
  }
  paragraphStyleName: string
}

type NormalizedFigure = Omit<Figure, 'filename' | 'location' | 'verticalPosition' | 'horizontalPosition'> & {
  filename: string
  location: string
  verticalPosition: number | string
  horizontalPosition: number | string
}

interface FormValues {
  figures: NormalizedFigure[]
  availableFigures: NormalizedFigure[]
  halfSizeImageCharacterSetting: HalfSizeImageCharacterSetting
}

type EventHandler = {
  onSubmit: (values: FormValues) => Promise<unknown>
}

interface ArticleFiguresDetailBodyProps {
  enumValues: EnumValues
  enums: Enums
  inputDisabled: boolean
  layoutable: Layoutable
  article: Article
  baseUri: string
}

const ArticleFiguresDetailBody: React.FC<ArticleFiguresDetailBodyProps> = (props) => {
  return (
    <FieldArray name='figures'>{(arrayHelpers) => <FigureFields arrayHelpers={arrayHelpers} {...props} />}</FieldArray>
  )
}

interface Props {
  article: Article
  layoutable: Layoutable
  layoutableId: string | number
  layoutableType: string
  holderArticle?: Article
  enumValues: EnumValues
  baseUri: string
  inputDisabled: boolean
  eventHandlerRef: React.RefObject<EventHandler | null>
}

export const ArticleFiguresDetail: React.FC<Props> = ({
  article,
  layoutable,
  layoutableId,
  layoutableType,
  holderArticle,
  enumValues,
  baseUri,
  inputDisabled,
  eventHandlerRef
}) => {
  const enums: Enums = {
    positionBases: {
      horizontalPositionBase: enumValues.figureHorizontalPositionBases,
      verticalPositionBase: enumValues.figureVerticalPositionBases
    },
    positionUnitName: {
      verticalPosition: '段目',
      horizontalPosition: '行目'
    },
    paragraphStyleName: getParagraphStyleName(layoutable.setting, 'caption') ?? ''
  }
  const commonProps: ArticleFiguresDetailBodyProps = {
    enumValues,
    enums,
    inputDisabled,
    layoutable,
    article,
    baseUri
  }

  const normalizeFigureRecord = (f: Figure): NormalizedFigure => {
    return {
      ...f,
      filename: f.filename ?? '',
      location: f.location ?? 'dummy',
      verticalPosition: nullishValueToValue(f.verticalPosition),
      horizontalPosition: nullishValueToValue(f.horizontalPosition)
    }
  }

  const halfSizeImageCharacterSetting = (article?.halfSizeImageCharacterArticleSetting ??
    layoutable.setting.halfSizeImageCharacterSetting) as HalfSizeImageCharacterSetting & {
    id?: unknown
    articleId?: unknown
  }
  const { id: _id, articleId: _articleId, ...noIdHalfSizeImageCharacterSetting } = halfSizeImageCharacterSetting

  const context = useFormikContext<Partial<FormValues>>()
  const formikReset = useFormikReset()
  const dispatch = useDispatch<ThunkDispatch<unknown, unknown, UnknownAction>>()

  useEffect(() => {
    const initialValues: FormValues = {
      figures: (article.figures ?? []).map(normalizeFigureRecord),
      availableFigures: (holderArticle?.figures ?? []).map(normalizeFigureRecord),
      halfSizeImageCharacterSetting: noIdHalfSizeImageCharacterSetting
    }

    context.resetForm({ values: initialValues })

    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [])

  const focusElementRef = useRef<HTMLSpanElement>(null)

  useImperativeHandle(eventHandlerRef, () => ({
    onSubmit: async (values: FormValues) => {
      const figuresChanged = !isEqual(context.initialValues.figures, values.figures)
      const availableChanged = !isEqual(context.initialValues.availableFigures, values.availableFigures)
      const promises: Promise<unknown>[] = []

      if (figuresChanged || availableChanged) {
        const initialIds = [
          ...(context.initialValues.figures ?? []),
          ...(context.initialValues.availableFigures ?? [])
        ].map((f) => f.id)
        const currentIds = [...values.figures, ...values.availableFigures].map((f) => f.id)
        const idsToDelete = initialIds.filter((id) => !currentIds.includes(id))

        const figures = values.figures.map((f) => ({
          ...f,
          articleId: article.id
        }))
        const availableFigures = values.availableFigures.map((f) => {
          return { ...f, articleId: holderArticle?.id ?? null }
        })
        const upsert = [...figures, ...availableFigures].map((f) => ({
          ...f,
          id: String(f.id).startsWith('null') ? null : f.id,
          location: f.filename.length > 0 ? f.location : '',
          verticalPosition: nullishValueToInternalValue(f.verticalPosition),
          horizontalPosition: nullishValueToInternalValue(f.horizontalPosition)
        }))
        const operations = {
          upsert,
          delete: idsToDelete.map((id) => ({ id }))
        }
        const params = {
          layoutableType,
          layoutableId,
          layoutableLockVersion: layoutable?.lockVersion,
          operations
        }
        promises.push(
          dispatch(bulkOperateFigures(params as Parameters<typeof bulkOperateFigures>[0])).then(() => {
            formikReset.resetForm({ values })
          })
        )
      }

      const halfSizeImageCharacterSettingChanged = !isEqual(
        context.initialValues.halfSizeImageCharacterSetting,
        values.halfSizeImageCharacterSetting
      )

      if (halfSizeImageCharacterSettingChanged) {
        console.log('halfSizeImageCharacterSettingChanged')

        const newArticle = {
          ...article,
          halfSizeImageCharacterArticleSetting: values.halfSizeImageCharacterSetting
        }
        const props = {
          layoutableType,
          layoutableId,
          layoutableLockVersion: layoutable?.lockVersion,
          article: newArticle,
          successAlertBody: '写真・図の変更が保存されました',
          loadedBinaryFile: undefined,
          deleteFile: undefined,
          auxiliaryLine: null
        }
        const submitParam = getSubmitParamForArticleUpsert(props) as Parameters<typeof modifyArticle>[0]
        promises.push(
          dispatch(modifyArticle(submitParam)).then(() => {
            formikReset.resetForm({ values })
          })
        )
      }

      if (promises.length === 0) {
        return Promise.resolve()
      }

      return Promise.all(promises)
    }
  }))

  useLayoutEffect(() => {
    const timeoutId = setTimeout(() => {
      focusElementRef.current?.focus()
      focusElementRef.current?.blur()
    }, 2)
    return () => {
      clearTimeout(timeoutId)
    }
  }, [context.initialValues])

  if ((context.values as Partial<FormValues> | undefined)?.figures === undefined) {
    return undefined
  }

  return (
    <>
      <Offcanvas.Header>
        <Offcanvas.Title className='detail-offcanvas-title'>
          写真・図
          <ArticleNameTitle article={article} />
        </Offcanvas.Title>
      </Offcanvas.Header>
      {!isAreaArticleType(article.articleType) && (
        <div className='m-1'>
          <HalfSizeImageCharacterSettingFields layoutable={layoutable} isDisabled={inputDisabled} />
        </div>
      )}
      <div
        className='flex-grow-1 overflow-auto m-1'
        style={{
          borderStyle: 'solid',
          borderWidth: '1px',
          borderColor: 'var(--bs-border-color)'
        }}
      >
        <Offcanvas.Body className='article-figures-detail-offcanvas-body'>
          <span
            ref={focusElementRef}
            tabIndex={-1}
            aria-hidden='true'
            style={{
              opacity: 0,
              height: 0,
              width: 0,
              top: 0,
              left: 0
            }}
          />
          <ArticleFiguresDetailBody {...commonProps} />
        </Offcanvas.Body>
      </div>
    </>
  )
}
